import { useCallback, useRef, useState } from "react";

const copyWithFallback = async (text: string) => {
  if (navigator.clipboard?.writeText && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "fixed";
  textArea.style.left = "-9999px";
  textArea.style.top = "0";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textArea);

  if (!copied) {
    throw new Error("Clipboard API not supported");
  }
};

export const useCopyToClipboard = () => {
  const [isCopying, setIsCopying] = useState(false);
  const inFlight = useRef(false);

  const copy = useCallback(
    async (
      text: string,
      onSuccess?: () => void,
      onError?: (error: Error) => void,
    ): Promise<void> => {
      if (inFlight.current) return;

      inFlight.current = true;
      setIsCopying(true);

      try {
        await copyWithFallback(text);
        onSuccess?.();
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Unknown clipboard error");
        onError?.(error);
      } finally {
        setTimeout(() => {
          inFlight.current = false;
          setIsCopying(false);
        }, 500);
      }
    },
    [],
  );

  return { isCopying, copy };
};
